import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import AuthContext from "../../context/AuthContext";
import LanguageContext from "../../context/LanguageContext";
import { API_URL } from "../../components/api";
import { formatDateTime } from "../../../utils/dateFormat";
import Payment from "../Collection/payment";
import "./Payments.css";


function PaymentsToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}


const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });


function Payments() {
  const { user } = useContext(AuthContext);
  const { direction, t } = useContext(LanguageContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showPayment, setShowPayment] = useState(false);

  const fetchPayments = useCallback(async () => {
    setLoading(true);

    try {
      const authTokens = JSON.parse(
        localStorage.getItem("authTokens")
      );
      const response = await axios.get(
        `${API_URL}/inventory/payments/`,
        {
          headers: {
            Authorization: `Bearer ${authTokens.access}`,
          },
          params: {
            history: true,
            username: user.username,
          },
        }
      );

      if (response.status === 200) {
        setRows(response.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [user.username]);

  React.useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const columns = useMemo(
    () => [
      {
        field: "payment_id",
        headerName: t("payments.paymentId"),
        minWidth: 120,
      },
      {
        field: "date",
        headerName: t("payments.date"),
        minWidth: 190,
        valueFormatter: (params) => formatDateTime(params.value),
      },
      {
        field: "type",
        headerName: t("payments.type"),
        minWidth: 120,
      },
      {
        field: "account_holder",
        headerName: t("payments.accountHolder"),
        minWidth: 190,
        flex: 1,
      },
      {
        field: "supplier",
        headerName: t("payments.supplier"),
        minWidth: 180,
        flex: 1,
      },
      {
        field: "paid_usd",
        headerName: t("payments.paidUsd"),
        minWidth: 140,
        valueFormatter: (params) => formatAmount(params.value),
      },
      {
        field: "paid_lbp",
        headerName: t("payments.paidLbp"),
        minWidth: 150,
        valueFormatter: (params) => formatAmount(params.value),
      },
      {
        field: "returned_lbp",
        headerName: t("payments.returnedLbp"),
        minWidth: 170,
        valueFormatter: (params) => formatAmount(params.value),
      },
      {
        field: "description",
        headerName: t("payments.description"),
        minWidth: 240,
        flex: 1,
      },
      {
        field: "created_by",
        headerName: t("payments.createdBy"),
        minWidth: 140,
      },
      {
        field: "drawer",
        headerName: t("payments.drawer"),
        minWidth: 160,
      },
      {
        field: "status",
        headerName: t("payments.status"),
        minWidth: 130,
      },
    ],
    [t]
  );

  const filteredRows = useMemo(() => {
    const words = search.toLowerCase().trim().split(/ +/);

    if (!search.trim()) {
      return rows;
    }

    return rows.filter((row) => {
      const searchable = Object.values(row)
        .join(" ")
        .toLowerCase();

      return words.every((word) => searchable.includes(word));
    });
  }, [rows, search]);

  return (
    <div className="payments-page" dir={direction}>
      <div className="payments-page-actions">
        <button
          type="button"
          className="payments-page-button"
          dir={direction}
          onClick={() => setShowPayment(true)}
        >
          {t("payments.newPayment")}
        </button>
      </div>

      <div className="payments-grid-panel">
        <input
          type="text"
          className="payments-search"
          value={search}
          placeholder={t("payments.search")}
          onChange={(event) => setSearch(event.target.value)}
        />

        <div className="payments-grid">
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={(row) => row.payment_id}
            loading={loading}
            pageSize={25}
            rowsPerPageOptions={[25, 50, 100]}
            components={{ Toolbar: PaymentsToolbar }}
            disableSelectionOnClick
          />
        </div>
      </div>

      <Modal
        show={showPayment}
        onHide={() => setShowPayment(false)}
        className="modal-lg accounting-payment-modal"
      >
        <div className="forma">
          <Modal.Header closeButton>
            <Modal.Title>{t("payments.newPayment")}</Modal.Title>
          </Modal.Header>
          <Payment onPaymentCreated={fetchPayments} />
        </div>
      </Modal>
    </div>
  );
}


export default Payments;
